import { type FC } from 'react';
import { InlineNotification } from '@carbon/react';
import type { StagedWell } from 'types/well-workspace';
import {
  FieldGrid,
  fieldNumber,
  fieldText,
  type Field,
} from 'components/well-cards';
import { WorkspaceCard, type WellScope } from 'components/workspace-cards';
import { Link } from 'components/link';
import { LinkCell } from 'components/link-cell';
import { InlineField } from './inline-field';
import {
  IDENTITY_LABELS,
  IDENTITY_SERVER_KEY,
  type IdentityAutosave,
  type IdentityField,
  type useOperatorOptions,
} from '../hooks';

interface Props {
  well: StagedWell;
  scope: WellScope;
  /**
   * The card's own editing. Its `editable` is what decides whether a row is writable at
   * all — false on a promoted workspace, which is frozen, and on a well with no staged row
   * yet, since there is no id to address the edit to.
   */
  autosave: IdentityAutosave;
  /** Suggestions for Current Operator, read from the well index as the reader types. */
  operators: ReturnType<typeof useOperatorOptions>;
}

/**
 * Read-only header attributes, in the mock's order.
 *
 * This set differs from the Well page's Well Details card: on the workspace the lifecycle
 * dates live in Well Status, and this card carries the location and depth attributes instead.
 *
 * UWI is read-only whatever the workspace: the backend generates it (a created well gets a
 * synthetic, non-numeric one) and a user never types it. Lease Name and Well Number are the
 * two fields SRP Well Name derives from — there is no name column, so the name itself is
 * never written, only shown.
 * @param {StagedWell} well The staged well.
 * @param {IdentityAutosave} autosave Which rows are writable, and the commit.
 * @param {object} operators Suggestions for the Current Operator combo.
 * @return {Field[]} The fields, in display order.
 */
const fieldsFor = (
  well: StagedWell,
  autosave: IdentityAutosave,
  operators: ReturnType<typeof useOperatorOptions>
): Field[] => {
  const editable = (field: IdentityField): Field => {
    const readOnly = fieldText(autosave.values[field]);
    /* Current Operator links to its entity where the catalog knows one, as it does on the
       Well page and in the map popup. Only on the row the reader cannot edit: an editable
       row shows the combo, and a link inside it would swallow the click that starts an
       edit. A promoted workspace and a member well with no staged row both land here. */
    const display =
      field === 'currentOperator' && well.currentOperatorHref ? (
        <LinkCell
          value={autosave.values.currentOperator}
          href={well.currentOperatorHref}
        />
      ) : (
        readOnly
      );

    return {
      label: IDENTITY_LABELS[field],
      // The same control the Well Status card uses, so the two read as one page: the
      // wash says the row is editable, the pencil says where to click, and the commit
      // autosaves. A row that cannot be written renders as plain text.
      value: autosave.editable(field) ? (
        <InlineField
          row={field}
          label={IDENTITY_LABELS[field]}
          /* Operator is a combo, not a plain field: the catalog already knows most of the
             answers, and the mock offers them. The other two are a lease and a number
             nobody can suggest. */
          kind={field === 'currentOperator' ? 'combo' : 'text'}
          items={field === 'currentOperator' ? operators.items : undefined}
          onSearchChange={
            field === 'currentOperator' ? operators.search : undefined
          }
          placeholder={
            field === 'currentOperator' ? 'Search operators' : undefined
          }
          value={autosave.values[field]}
          readOnly={readOnly}
          onCommit={autosave.commit}
          dirty={
            autosave.pending.has(field) ||
            (well.userEditedFields ?? well.editedFields).includes(
              IDENTITY_SERVER_KEY[field]
            )
          }
        />
      ) : (
        display
      ),
    };
  };

  return [
    // The UWI is the way to the well's own page, in a new tab so the workspace stays put.
    // A well authored here has no canonical page to reach yet, so it reads as plain text
    // until it promotes.
    {
      label: 'UWI',
      value:
        well.wellId === null ? (
          fieldText(well.uwi)
        ) : (
          <Link to={`/wells/${well.uwi}`} openInNewTab>
            {well.uwi}
          </Link>
        ),
    },
    { label: 'SRP Well Name', value: fieldText(well.name) },
    editable('leaseName'),
    editable('wellNumber'),
    editable('currentOperator'),
    { label: 'Hole Direction', value: fieldText(well.holeDirection) },
    { label: 'State', value: fieldText(well.stateAbbreviation) },
    { label: 'County', value: fieldText(well.county) },
    { label: 'SIM Landing Zone', value: fieldText(well.srpLandingZone) },
    {
      label: 'Vendor Measured Depth',
      value: fieldNumber(well.measuredDepthFeet),
    },
    {
      label: 'Vendor True Vertical Depth',
      value: fieldNumber(well.trueVerticalDepthFeet),
    },
    // Last Production Date lives on Well Status with the other lifecycle dates (UAT).
  ];
};

export const WellDetailsCard: FC<Props> = ({
  well,
  scope,
  autosave,
  operators,
}) => (
  <WorkspaceCard title="Well Details" scope={scope}>
    {autosave.error && (
      <InlineNotification
        kind="error"
        title="Could not save the well"
        subtitle={autosave.error}
        lowContrast
        onCloseButtonClick={autosave.dismissError}
      />
    )}
    <FieldGrid fields={fieldsFor(well, autosave, operators)} />
  </WorkspaceCard>
);
